/**
 * BrowseSpacesView
 * ----------------
 *
 * In-panel host for `BrowseSpacesFullscreen`. Rendered from `main.tsx` when
 * `activeMenu === 'browse-spaces'` (mirrors the existing `BrowseAgentsView`
 * wrapper, which renders the `BrowseAgentsFullscreen`/`BrowseAgentsSidebar`
 * pair when `activeMenu === 'agents'`).
 *
 * Why this wrapper exists in `conversation-assistant` rather than
 * `rovo-spaces`: the dumb `BrowseSpacesFullscreen` component lives in
 * `@atlassian/rovo-spaces`, which intentionally does NOT depend on
 * `conversation-assistant-store` (the ui-store with `activeMenu`,
 * `selectedSpace`, etc.) or `conversation-assistant`'s chat-store. Putting the
 * store wiring here keeps `rovo-spaces` host-agnostic so that FE-B5b
 * (Townsquare route + view registration) and FE-B5c (Confluence/Jira embed
 * wiring) can mount `BrowseSpacesFullscreen` against their own routing /
 * navigation primitives without dragging in conversation-assistant state.
 *
 * All behaviour is gated by `rovo_chat_spaces_experiment` at the call-site
 * (`main.tsx`).
 */
import React, { useCallback, useMemo } from 'react';

import { usePublish } from '@atlaskit/rovo-triggers/main';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { useChatContextStoreActions } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import type { Conversation } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import {
	type EmptyChatLaunchProjectContext,
	useSelectedSpaceSelector,
	useUIStoreActions,
	useUIStoreSubscription,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import type { RovoNavigationConversation } from '@atlassian/rovo-spaces/types/rovo-navigation-conversation';
import { BrowseSpacesFullscreen } from '@atlassian/rovo-spaces/ui/BrowseSpacesFullscreen';

import { useGlobalConfig } from '../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../common/utils/ui-config/useUIConfig';
import { useAssistanceServiceParams } from '../../common/utils/use-assistance-service-params';
import { useChatStore } from '../../controllers/chat-store/useChatStore';
import { useChatStoreActions } from '../../controllers/chat-store/useChatStoreActions';
import { SUGGESTIONS_TAB_ID } from '../chat-history/rovo-tabs/constants';

type BrowseSpacesViewProps = {
	/**
	 * Chrome row (`←` `≡` `Rovo` `⋯` `X`) passed in from `main.tsx`. Sidebar
	 * hosts need it for local panel navigation; fullscreen hosts use their own
	 * global chrome, so this wrapper intentionally suppresses it there.
	 */
	chatRefreshHeader?: React.ReactNode;
};

const toRovoNavigationConversation = (conversation: Conversation): RovoNavigationConversation => ({
	...conversation,
	isEmpty: !conversation.lastMessage?.trim(),
});

/**
 * Wraps `BrowseSpacesFullscreen` with the store wiring needed to route a row
 * click into "open this space's chat view" (mirrors `handleSelectSpace` from
 * `rovo-side-navigation`).
 */
export const BrowseSpacesView = ({
	chatRefreshHeader,
}: BrowseSpacesViewProps): React.JSX.Element => {
	const { siteId } = useGlobalConfig();
	const { fullScreen } = useUIConfig();
	const assistanceServiceParams = useAssistanceServiceParams({
		touchpointSource: 'browse-spaces-panel',
	});
	useUIStoreSubscription();
	const {
		clearSelectedSpace,
		setSelectedSpace,
		setActiveMenu,
		setActiveTabId,
		setEmptyChatLaunch,
	} = useUIStoreActions();
	const { setAIFeatureContext } = useChatContextStoreActions();
	const [{ selectedSpace }] = useSelectedSpaceSelector();
	const [{ conversations, currentConversationId }] = useChatStore();
	const { setSelectedConversation } = useChatStoreActions();
	const publish = usePublish('ai-mate');
	const localSpaceConversations = useMemo(
		() => Object.values(conversations).map(toRovoNavigationConversation),
		[conversations],
	);

	const handleSelectSpace = useCallback(
		(space: { id: string; name: string; emoji?: string | null }) => {
			// Mirrors `handleSelectSpace` in `rovo-side-navigation/index.tsx`
			// (without the `closeNav` branch — this view is rendered inside the
			// chat panel itself, so there's nothing to close).
			setSelectedSpace({
				id: space.id,
				name: space.name,
				emoji: space.emoji ?? undefined,
				entryPoint: 'sidebar',
			});
			setActiveTabId(SUGGESTIONS_TAB_ID);
			setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID });

			const projectContext: EmptyChatLaunchProjectContext = {
				projectId: space.id,
				projectName: space.name,
				// projectUrl is intentionally empty — Rovo Spaces do not have
				// a canonical URL. Matches the established pattern in
				// `rovo-side-navigation/handleSelectSpace` (line ~184) and
				// `chat-history/index.tsx` (line ~233 / 356). Downstream
				// consumers (e.g. `pickContextBannerUrl`) already treat an
				// empty string as "no URL available" and fall back gracefully.
				projectUrl: '',
				spaceId: space.id,
			};
			setAIFeatureContext({
				contextKey: 'projectContext',
				setContext: <T,>(_: T): T => projectContext as T,
			});
			setEmptyChatLaunch({
				replaceProviderActions: true,
				projectContext,
			});
			publish({
				type: 'space-selected',
				source: 'browse-spaces-view',
				data: {
					spaceId: space.id,
					title: space.name,
					emoji: space.emoji ?? '',
					entryPoint: 'sidebar',
				},
			});

			// Close the browse view by flipping activeMenu back to 'none' — this
			// hands focus back to the regular chat surface, which now shows the
			// selected space's empty chat with starters.
			setActiveMenu('none');
		},
		[
			setSelectedSpace,
			setActiveTabId,
			setSelectedConversation,
			setAIFeatureContext,
			setEmptyChatLaunch,
			publish,
			setActiveMenu,
		],
	);

	const handleSelectSpaceChat = useCallback(
		(chat: RovoNavigationConversation) => {
			clearSelectedSpace();
			setSelectedConversation({
				conversationId: chat.id,
				conversationMetadata: chat,
			});
			setActiveMenu('none');
		},
		[clearSelectedSpace, setActiveMenu, setSelectedConversation],
	);

	const handleViewRecommendedSpaces = useCallback(() => {
		setActiveMenu('recommended-spaces');
		setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID });
	}, [setActiveMenu, setSelectedConversation]);

	const layout = fullScreen ? 'fullscreen' : 'sidebar';

	return (
		<BrowseSpacesFullscreen
			chatRefreshHeader={fullScreen ? undefined : chatRefreshHeader}
			layout={layout}
			cloudId={siteId}
			product={assistanceServiceParams.product ?? 'rovo'}
			experienceId={assistanceServiceParams.experienceId ?? 'ai-mate'}
			assistanceServiceParams={assistanceServiceParams}
			onSelectSpace={handleSelectSpace}
			onSelectSpaceChat={handleSelectSpaceChat}
			onViewRecommendedSpaces={handleViewRecommendedSpaces}
			localSpaceConversations={localSpaceConversations}
			activeSpaceId={selectedSpace?.id}
			activeConversationId={currentConversationId}
		/>
	);
};
